import type React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { createContext, ReactNode, useContext } from 'react';

type GridSize = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
type GridSpacing = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

// Container variants using CVA with negative margins for spacing
const gridContainerVariants = cva('flex', {
  variants: {
    direction: {
      row: 'flex-row',
      'row-reverse': 'flex-row-reverse',
      column: 'flex-col',
      'column-reverse': 'flex-col-reverse',
    },
    justifyContent: {
      'flex-start': 'justify-start',
      center: 'justify-center',
      'flex-end': 'justify-end',
      'space-between': 'justify-between',
      'space-around': 'justify-around',
      'space-evenly': 'justify-evenly',
    },
    alignItems: {
      'flex-start': 'items-start',
      center: 'items-center',
      'flex-end': 'items-end',
      stretch: 'items-stretch',
      baseline: 'items-baseline',
    },
    wrap: {
      nowrap: 'flex-nowrap',
      wrap: 'flex-wrap',
      'wrap-reverse': 'flex-wrap-reverse',
    },
    // Negative margins for container spacing (MUI approach)
    spacing: {
      0: '',
      1: '',
      2: '',
      3: '',
      4: '',
      5: '',
      6: '',
      7: '',
      8: '',
      9: '',
      10: '',
    },
  },
  defaultVariants: {
    direction: 'row',
    wrap: 'wrap',
    spacing: 0,
  },
  compoundVariants: [
    {
      direction: ['row', 'row-reverse'],
      spacing: 1,
      className: '-ml-1',
    },
    {
      direction: ['row', 'row-reverse'],
      spacing: 2,
      className: '-ml-2',
    },
    {
      direction: ['row', 'row-reverse'],
      spacing: 3,
      className: '-ml-3',
    },
    {
      direction: ['row', 'row-reverse'],
      spacing: 4,
      className: '-ml-4',
    },
    {
      direction: ['row', 'row-reverse'],
      spacing: 5,
      className: '-ml-5',
    },
    {
      direction: ['row', 'row-reverse'],
      spacing: 6,
      className: '-ml-6',
    },
    {
      direction: ['row', 'row-reverse'],
      spacing: 7,
      className: '-ml-7',
    },
    {
      direction: ['row', 'row-reverse'],
      spacing: 8,
      className: '-ml-8',
    },
    {
      direction: ['row', 'row-reverse'],
      spacing: 9,
      className: '-ml-9',
    },
    {
      direction: ['row', 'row-reverse'],
      spacing: 10,
      className: '-ml-10',
    },
    {
      direction: ['column', 'column-reverse'],
      spacing: 1,
      className: '-mt-1',
    },
    {
      direction: ['column', 'column-reverse'],
      spacing: 2,
      className: '-mt-2',
    },
    {
      direction: ['column', 'column-reverse'],
      spacing: 3,
      className: '-mt-3',
    },
    {
      direction: ['column', 'column-reverse'],
      spacing: 4,
      className: '-mt-4',
    },
    {
      direction: ['column', 'column-reverse'],
      spacing: 5,
      className: '-mt-5',
    },
    {
      direction: ['column', 'column-reverse'],
      spacing: 6,
      className: '-mt-6',
    },
    {
      direction: ['column', 'column-reverse'],
      spacing: 7,
      className: '-mt-7',
    },
    {
      direction: ['column', 'column-reverse'],
      spacing: 8,
      className: '-mt-8',
    },
    {
      direction: ['column', 'column-reverse'],
      spacing: 9,
      className: '-mt-9',
    },
    {
      direction: ['column', 'column-reverse'],
      spacing: 10,
      className: '-mt-10',
    },
  ],
});

// Item variants with padding for spacing
const gridItemVariants = cva('', {
  variants: {
    // Padding for items (matches container negative margin)
    spacing: {
      0: '',
      1: 'pt-1 pl-1',
      2: 'pt-2 pl-2',
      3: 'pt-3 pl-3',
      4: 'pt-4 pl-4',
      5: 'pt-5 pl-5',
      6: 'pt-6 pl-6',
      7: 'pt-7 pl-7',
      8: 'pt-8 pl-8',
      9: 'pt-9 pl-9',
      10: 'pt-10 pl-10',
    },
  },
});

// Create a context to pass spacing down to items
const GridContext = createContext<{ spacing: GridSpacing }>({ spacing: 0 });

const gridSizeVariants = cva('flex-none', {
  variants: {
    span: {
      1: 'w-1/12',
      2: 'w-2/12',
      3: 'w-3/12',
      4: 'w-4/12',
      5: 'w-5/12',
      6: 'w-6/12',
      7: 'w-7/12',
      8: 'w-8/12',
      9: 'w-9/12',
      10: 'w-10/12',
      11: 'w-11/12',
      12: 'w-12/12',
    },
    gridSize: {
      xs: '',
      sm: '',
      md: '',
      lg: '',
      xl: '',
    },
  },
  defaultVariants: {
    span: 4,
  },
  compoundVariants: [
    {
      span: 1,
      gridSize: 'xs',
      className: 'xs:w-1/12',
    },
    {
      span: 2,
      gridSize: 'xs',
      className: 'xs:w-2/12',
    },
    {
      span: 3,
      gridSize: 'xs',
      className: 'xs:w-3/12',
    },
    {
      span: 4,
      gridSize: 'xs',
      className: 'xs:w-4/12',
    },
    {
      span: 5,
      gridSize: 'xs',
      className: 'xs:w-5/12',
    },
    {
      span: 6,
      gridSize: 'xs',
      className: 'xs:w-6/12',
    },
    {
      span: 7,
      gridSize: 'xs',
      className: 'xs:w-7/12',
    },
    {
      span: 8,
      gridSize: 'xs',
      className: 'xs:w-8/12',
    },
    {
      span: 9,
      gridSize: 'xs',
      className: 'xs:w-9/12',
    },
    {
      span: 10,
      gridSize: 'xs',
      className: 'xs:w-10/12',
    },
    {
      span: 11,
      gridSize: 'xs',
      className: 'xs:w-11/12',
    },
    {
      span: 12,
      gridSize: 'xs',
      className: 'xs:w-full',
    },
    {
      span: 1,
      gridSize: 'sm',
      className: 'sm:w-1/12 md:w-1/12',
    },
    {
      span: 2,
      gridSize: 'sm',
      className: 'sm:w-2/12 md:w-2/12',
    },
    {
      span: 3,
      gridSize: 'sm',
      className: 'sm:w-3/12 md:w-3/12',
    },
    {
      span: 4,
      gridSize: 'sm',
      className: 'sm:w-4/12 md:w-4/12',
    },
    {
      span: 5,
      gridSize: 'sm',
      className: 'sm:w-5/12 md:w-5/12',
    },
    {
      span: 6,
      gridSize: 'sm',
      className: 'sm:w-6/12 md:w-6/12',
    },
    {
      span: 7,
      gridSize: 'sm',
      className: 'sm:w-7/12 md:w-7/12',
    },
    {
      span: 8,
      gridSize: 'sm',
      className: 'sm:w-8/12 md:w-8/12',
    },
    {
      span: 9,
      gridSize: 'sm',
      className: 'sm:w-9/12 md:w-9/12',
    },
    {
      span: 10,
      gridSize: 'sm',
      className: 'sm:w-10/12 md:w-10/12',
    },
    {
      span: 11,
      gridSize: 'sm',
      className: 'sm:w-11/12 md:w-11/12',
    },
    {
      span: 12,
      gridSize: 'sm',
      className: 'sm:w-full md:w-full',
    },
    {
      span: 1,
      gridSize: 'md',
      className: 'lg:w-1/12',
    },
    {
      span: 2,
      gridSize: 'md',
      className: 'lg:w-2/12',
    },
    {
      span: 3,
      gridSize: 'md',
      className: 'lg:w-3/12',
    },
    {
      span: 4,
      gridSize: 'md',
      className: 'lg:w-4/12',
    },
    {
      span: 5,
      gridSize: 'md',
      className: 'lg:w-5/12',
    },
    {
      span: 6,
      gridSize: 'md',
      className: 'lg:w-6/12',
    },
    {
      span: 7,
      gridSize: 'md',
      className: 'lg:w-7/12',
    },
    {
      span: 8,
      gridSize: 'md',
      className: 'lg:w-8/12',
    },
    {
      span: 9,
      gridSize: 'md',
      className: 'lg:w-9/12',
    },
    {
      span: 10,
      gridSize: 'md',
      className: 'lg:w-10/12',
    },
    {
      span: 11,
      gridSize: 'md',
      className: 'lg:w-11/12',
    },
    {
      span: 12,
      gridSize: 'md',
      className: 'lg:w-full',
    },
    {
      span: 1,
      gridSize: 'lg',
      className: 'xl:w-1/12',
    },
    {
      span: 2,
      gridSize: 'lg',
      className: 'xl:w-2/12',
    },
    {
      span: 3,
      gridSize: 'lg',
      className: 'xl:w-3/12',
    },
    {
      span: 4,
      gridSize: 'lg',
      className: 'xl:w-4/12',
    },
    {
      span: 5,
      gridSize: 'lg',
      className: 'xl:w-5/12',
    },
    {
      span: 6,
      gridSize: 'lg',
      className: 'xl:w-6/12',
    },
    {
      span: 7,
      gridSize: 'lg',
      className: 'xl:w-7/12',
    },
    {
      span: 8,
      gridSize: 'lg',
      className: 'xl:w-8/12',
    },
    {
      span: 9,
      gridSize: 'lg',
      className: 'xl:w-9/12',
    },
    {
      span: 10,
      gridSize: 'lg',
      className: 'xl:w-10/12',
    },
    {
      span: 11,
      gridSize: 'lg',
      className: 'xl:w-11/12',
    },
    {
      span: 12,
      gridSize: 'lg',
      className: 'xl:w-full',
    },
    {
      span: 1,
      gridSize: 'xl',
      className: '2xl:w-1/12',
    },
    {
      span: 2,
      gridSize: 'xl',
      className: '2xl:w-2/12',
    },
    {
      span: 3,
      gridSize: 'xl',
      className: '2xl:w-3/12',
    },
    {
      span: 4,
      gridSize: 'xl',
      className: '2xl:w-4/12',
    },
    {
      span: 5,
      gridSize: 'xl',
      className: '2xl:w-5/12',
    },
    {
      span: 6,
      gridSize: 'xl',
      className: '2xl:w-6/12',
    },
    {
      span: 7,
      gridSize: 'xl',
      className: '2xl:w-7/12',
    },
    {
      span: 8,
      gridSize: 'xl',
      className: '2xl:w-8/12',
    },
    {
      span: 9,
      gridSize: 'xl',
      className: '2xl:w-9/12',
    },
    {
      span: 10,
      gridSize: 'xl',
      className: '2xl:w-10/12',
    },
    {
      span: 11,
      gridSize: 'xl',
      className: '2xl:w-11/12',
    },
    {
      span: 12,
      gridSize: 'xl',
      className: '2xl:w-full',
    },
  ],
});

interface GridProps
  extends
    VariantProps<typeof gridContainerVariants>,
    VariantProps<typeof gridItemVariants> {
  children?: React.ReactNode;
  className?: string;
  xs?: GridSize;
  sm?: GridSize;
  md?: GridSize;
  lg?: GridSize;
  xl?: GridSize;
}

interface GridProps extends VariantProps<typeof gridContainerVariants> {
  children?: ReactNode;
  className?: string;
  container?: boolean;
  item?: boolean;
  xs?: GridSize;
  sm?: GridSize;
  md?: GridSize;
  lg?: GridSize;
  xl?: GridSize;
  zeroMinWidth?: boolean;
}

export const Grid: React.FC<GridProps> = ({ item, container: _, ...props }) => {
  if (item) return <GridItem {...props} />;

  return <GridContainer {...props} />;
};

function GridDiv({
  children,
  className,
  ...props
}: Omit<GridProps, 'container' | 'item'>) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  );
}

function GridContainer({
  className,
  spacing,
  ...props
}: Omit<GridProps, 'container' | 'item'>) {
  return (
    <GridContext.Provider value={{ spacing: spacing ?? 1 }}>
      <GridDiv
        className={cn(className, gridContainerVariants({ spacing }))}
        {...props}
      />
    </GridContext.Provider>
  );
}

function GridItem({
  xs,
  sm,
  md,
  lg,
  xl,
  className,
  zeroMinWidth,
  ...props
}: Omit<GridProps, 'container' | 'item'>) {
  const contextSpacing = useContext(GridContext).spacing;

  // Add padding that matches container's negative margin
  const itemSpacingClass = gridItemVariants({ spacing: contextSpacing ?? 1 });

  // Generate responsive width classes
  // const hasAnySize = [xs, sm, md, lg, xl].some((size) => size !== undefined);

  // if (!hasAnySize) {
  //   // No sizes specified - use flex-1 for equal distribution
  //   responsiveClasses = 'flex-1';
  // } else {
  //   const widthClasses = [];

  //   responsiveClasses = widthClasses.join(' ');
  // }

  const responsiveClasses = [
    gridSizeVariants({ span: 4 }),
    xs && gridSizeVariants({ span: xs, gridSize: 'xs' }),
    sm && gridSizeVariants({ span: sm, gridSize: 'sm' }),
    md && gridSizeVariants({ span: md, gridSize: 'md' }),
    lg && gridSizeVariants({ span: lg, gridSize: 'lg' }),
    xl && gridSizeVariants({ span: xl, gridSize: 'xl' }),
  ];

  return (
    <GridDiv
      className={cn(
        itemSpacingClass,
        responsiveClasses,
        zeroMinWidth && 'min-w-0',
        className,
      )}
      {...props}
    />
  );
}
